import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { prepareResponse, getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { ID_VERIFICATION_POLLING_INTERVAL, isIdVerificationLiveStatus } from '@/helpers/verificationHelpers';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { secureLinkEmailOtpApi } from '@/api/v1/secure-links/secureLinkEmailOtpApi';
import { startSecureLinkVerificationApi } from '@/api/v1/secure-links/startSecureLinkVerificationApi';
import { getSecureLinkVerificationStatusApi } from '@/api/v1/secure-links/getSecureLinkVerificationStatusApi';
import { resendSecureLinkVerificationApi } from '@/api/v1/secure-links/resendSecureLinkVerificationApi';

export const useSecureLinkVerification = (token: string | undefined, enabled: boolean) => {
  const [phone, setPhone] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [error, setError] = useState('');

  const { data: statusResponse, refetch: refetchStatus } = useQuery({
    queryKey: ['secure_link_verify_status', token],
    queryFn: async () => prepareResponse(await getSecureLinkVerificationStatusApi(token)),
    enabled: enabled && !!token,
    refetchInterval: (query) =>
      isIdVerificationLiveStatus(query.state.data?.body?.status) ? ID_VERIFICATION_POLLING_INTERVAL : false,
    refetchIntervalInBackground: true,
  });
  const verifyStatus = statusResponse?.body;

  const emailOtp = useMutation({
    mutationFn: async (code?: string) => prepareResponse(await secureLinkEmailOtpApi(token, code)),
    onSuccess(response, code) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      if (code) setEmailChecked(true);
      else setCodeSent(true);
    },
  });
  const start = useMutation({
    mutationFn: async () => prepareResponse(await startSecureLinkVerificationApi(token, phone)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      setShowPhoneForm(false);
      refetchStatus();
    },
  });
  const resend = useMutation({
    mutationKey: ['secure_link_verify_resend', token],
    mutationFn: async () => prepareResponse(await resendSecureLinkVerificationApi(token)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      showSnackbar('We sent a new verification link to your phone.');
      refetchStatus();
    },
  });

  useEffect(() => {
    if (verifyStatus?.status === 'failed') setError('Verification did not pass. You can try again.');
  }, [verifyStatus?.status]);

  return {
    verifyStatus,
    refetchStatus,
    verified: verifyStatus?.status === 'succeed',
    inProgress: isIdVerificationLiveStatus(verifyStatus?.status),
    resendIn: verifyStatus?.start_new_session_enable_after_sec ?? 0,
    phone,
    setPhone,
    emailCode,
    setEmailCode,
    emailChecked,
    codeSent,
    showPhoneForm,
    setShowPhoneForm,
    error,
    setError,
    emailOtp,
    start,
    resend,
  };
};
